export type Lang = 'he' | 'en' | 'ar' | 'ru';

export const FEATURES = [
	'font_size',
	'line_height',
	'letter_spacing',
	'word_spacing',
	'readable_font',
	'dyslexia_font',
	'contrast_high',
	'contrast_inverted',
	'contrast_grayscale',
	'highlight_links',
	'highlight_headings',
	'big_cursor',
	'stop_animations',
	'hide_images',
	'reading_guide',
	'text_to_speech',
	'keyboard_hints',
	'reset',
	'statement_link',
	'report_link',
] as const;

export type FeatureId = ( typeof FEATURES )[ number ];

export interface ToolbarConfig {
	features: Record< FeatureId, boolean >;
	position: 'start' | 'end' | 'left' | 'right';
	language: 'auto' | Lang;
	locale: string;
	shortcut: string;
	color: string;
	statementUrl: string;
	reportUrl: string;
	dyslexiaFontUrl: string;
}

const DEFAULT_COLOR = '#0b3d91';
const DEFAULT_SHORTCUT = 'Alt+Shift+A';

const safeUrl = ( value: unknown ): string => {
	const url = typeof value === 'string' ? value.trim() : '';
	return /^(https?:\/\/|mailto:|\/(?!\/))/i.test( url ) ? url : '';
};

export const parseShortcut = ( value: string ) => {
	const parts = value
		.split( '+' )
		.map( ( p ) => p.trim() )
		.filter( Boolean );
	const key = parts.pop() ?? '';
	const mods = parts.map( ( p ) => p.toLowerCase() );
	const known = [ 'alt', 'shift', 'ctrl', 'meta' ];
	if (
		key.length !== 1 ||
		! /^[a-z0-9]$/i.test( key ) ||
		mods.some( ( m ) => ! known.includes( m ) )
	) {
		return null;
	}
	if ( ! mods.some( ( m ) => m !== 'shift' ) ) {
		return null;
	}
	return {
		alt: mods.includes( 'alt' ),
		shift: mods.includes( 'shift' ),
		ctrl: mods.includes( 'ctrl' ),
		meta: mods.includes( 'meta' ),
		key: key.toUpperCase(),
	};
};

export const readConfig = ( doc: Document ): ToolbarConfig | null => {
	const node = doc.getElementById( 'nagish-toolbar-config' );
	if ( ! node?.textContent ) {
		return null;
	}
	let raw: Record< string, unknown >;
	try {
		raw = JSON.parse( node.textContent );
	} catch {
		return null;
	}
	const given = ( raw.features ?? {} ) as Record< string, unknown >;
	const features = {} as Record< FeatureId, boolean >;
	for ( const id of FEATURES ) {
		features[ id ] = given[ id ] === true;
	}
	const position = [ 'start', 'end', 'left', 'right' ].includes(
		raw.position as string
	)
		? ( raw.position as ToolbarConfig[ 'position' ] )
		: 'end';
	const language = [ 'he', 'en', 'ar', 'ru' ].includes(
		raw.language as string
	)
		? ( raw.language as Lang )
		: 'auto';
	const shortcut =
		typeof raw.shortcut === 'string' ? raw.shortcut : DEFAULT_SHORTCUT;
	return {
		features,
		position,
		language,
		locale: typeof raw.locale === 'string' ? raw.locale : '',
		shortcut:
			shortcut === '' || parseShortcut( shortcut )
				? shortcut
				: DEFAULT_SHORTCUT,
		color:
			typeof raw.color === 'string' && /^#[0-9a-f]{6}$/i.test( raw.color )
				? raw.color
				: DEFAULT_COLOR,
		statementUrl: safeUrl( raw.statementUrl ),
		reportUrl: safeUrl( raw.reportUrl ),
		dyslexiaFontUrl: safeUrl( raw.dyslexiaFontUrl ),
	};
};

export const pickLanguage = ( cfg: ToolbarConfig, pageLang: string ): Lang => {
	if ( cfg.language !== 'auto' ) {
		return cfg.language;
	}
	for ( const source of [ pageLang, cfg.locale ] ) {
		const base = source.toLowerCase().split( /[-_]/ )[ 0 ];
		if ( base === 'iw' || base === 'he' ) {
			return 'he';
		}
		if ( base === 'ar' || base === 'ru' || base === 'en' ) {
			return base;
		}
	}
	return 'en';
};

export const isRtl = ( lang: Lang ): boolean => lang === 'he' || lang === 'ar';

const channel = ( hex: string, i: number ): number => {
	const v = parseInt( hex.slice( 1 + i * 2, 3 + i * 2 ), 16 ) / 255;
	return v <= 0.03928 ? v / 12.92 : Math.pow( ( v + 0.055 ) / 1.055, 2.4 );
};

export const luminance = ( hex: string ): number =>
	0.2126 * channel( hex, 0 ) +
	0.7152 * channel( hex, 1 ) +
	0.0722 * channel( hex, 2 );

export const contrastRatio = ( a: string, b: string ): number => {
	const [ hi, lo ] = [ luminance( a ), luminance( b ) ].sort(
		( x, y ) => y - x
	) as [ number, number ];
	return ( hi + 0.05 ) / ( lo + 0.05 );
};

/**
 * Launcher colours: the icon colour is the better of white/black; below 4.5:1 the default colour is used.
 * @param bg
 */
export const launcherColors = ( bg: string ): { bg: string; fg: string } => {
	const pick = ( c: string ) =>
		contrastRatio( c, '#ffffff' ) >= contrastRatio( c, '#000000' )
			? '#ffffff'
			: '#000000';
	const fg = pick( bg );
	if ( contrastRatio( bg, fg ) >= 4.5 ) {
		return { bg, fg };
	}
	return { bg: DEFAULT_COLOR, fg: '#ffffff' };
};
